import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { DateTimeUtil } from "../../../utils";
import {
    MessageSquare,
    Info,
    CheckCircle,
    ClockIcon,
    ChevronRight,
    Pencil,
    XCircle,
    Loader2,
} from "lucide-react";
import { getApprovalStatusCheck, getApprovalStatusText } from "../../../utils/timeoff";
import { callPhpWorkflow } from "../../../redux/features/common";
import { getNotificationDetail } from "../../../redux/features/notifications";
import { EmployeeSchedulesMap, NotifDetailsDay } from "../../../types";
import { cancelTimeOffRequest, setTimeoffToUpdate, setTimeoffUpdateMode } from "../../../redux/features/timeoff";
import { useLocation, useNavigate } from "react-router";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout/slice";
import { NgwAvatar } from "../../../components/common";
import { ApprovalHistory } from "../../../components/notificationv2";

const PENDING_STORAGE_KEY = "notification_detail_pending";

const TimeoffNotificationScreen: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const navState = location.state as { notificationTransactionId?: string; employeeId?: number } | undefined;
    const [pendingFromStorage, setPendingFromStorage] = useState<{
        transactionId?: string;
        employeeId?: number;
    } | null>(() => {
        try {
            const raw = localStorage.getItem(PENDING_STORAGE_KEY);
            if (!raw) return null;
            const data = JSON.parse(raw) as { category?: string; transactionId?: string; employeeId?: number };
            return data?.transactionId != null
                ? { transactionId: data.transactionId, employeeId: data.employeeId }
                : null;
        } catch {
            return null;
        }
    });
    const transactionId = navState?.notificationTransactionId ?? pendingFromStorage?.transactionId;
    const employeeIdParam = navState?.employeeId ?? pendingFromStorage?.employeeId ?? undefined;
    const [notes, setNotes] = useState("");
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Fetch selected notification from Redux
    const { notificationDetail: data, isNotificationDetailLoading } = useSelector(
        (state: RootState) => state.notification
    );
    const user = useSelector((state: RootState) => state.user.user);
    const isCallingWorkflow = useSelector((state: RootState) => state.common.isCallingWorkflow);

    // When opened from SCH timeoff modal (state or localStorage for new window), fetch detail on this page
    useEffect(() => {
        if (!transactionId) return;
        const employeeId = employeeIdParam ?? user?.os_emp_account_id;
        if (!employeeId) return;
        dispatch(
            getNotificationDetail({
                notificationId: transactionId,
                employeeId: Number(employeeId),
            })
        );
        if (pendingFromStorage) {
            try {
                localStorage.removeItem(PENDING_STORAGE_KEY);
            } catch (_) {}
            setPendingFromStorage(null);
        }
    }, [transactionId, employeeIdParam, user?.os_emp_account_id, dispatch, pendingFromStorage]);

    if (isNotificationDetailLoading) {
        return (
            <div className="flex flex-col justify-center items-center h-screen gap-2">
                <div className="animate-spin w-10 h-10 border-4 border-gray-300 border-t-transparent rounded-full" />
                {isRefreshing && <p className="text-gray-600 text-sm">Refreshing...</p>}
            </div>
        );
    }

    // Show loading when we landed with transaction id but detail not loaded yet
    const isPendingFromNav = Boolean(transactionId && !data);
    if (isPendingFromNav) {
        return (
            <div className="flex flex-col justify-center items-center h-screen gap-2">
                <div className="animate-spin w-10 h-10 border-4 border-gray-300 border-t-transparent rounded-full" />
            </div>
        );
    }

    const refreshData = async () => {
        setIsRefreshing(true);
        await dispatch(
            getNotificationDetail({
                notificationId: data?.notification_id!,
                employeeId: Number(user.os_emp_account_id!),
            })
        );
        setIsRefreshing(false);
    };

    if (!data) return null;

    const handleAccept = async () => {
        const timeOffDetails = data?.time_off_details;
        if (!timeOffDetails) return;

        const timeOffRequestId = timeOffDetails?.time_off_request_id;
        // const employeeId = timeOffDetails?.requestor?.employee_id;
        const employeeId = user.os_emp_account_id!;

        if (!timeOffRequestId || !employeeId) return;

        const body = {
            workflow_code: data?.alert_json?.WF_CODE,
            status: 1,
            user_account_id: user?.userAccountId?.toString() ?? "",
            actor_id: user?.id?.toString() ?? "",
            reference_id: timeOffRequestId,
            result: { APPROVED: { [employeeId]: [timeOffRequestId] }, REJECTED: {} },
            notes,
            workflow_header_id: data?.alert_json?.WF_HEADER_ID,
            wf_txn_lines_id: data?.alert_json?.WF_TXN_LINES_ID,
        };

        await dispatch(callPhpWorkflow(body));
        refreshData();
        // navigate(-1);
    };

    const handleDecline = async () => {
        const timeOffDetails = data?.time_off_details;
        if (!timeOffDetails) return;

        const timeOffRequestId = timeOffDetails?.time_off_request_id;
        // const employeeId = timeOffDetails?.requestor?.employee_id;
        const employeeId = user.os_emp_account_id!;

        if (!timeOffRequestId || !employeeId) return;

        const body = {
            workflow_code: data?.alert_json?.WF_CODE,
            status: 0,
            user_account_id: user?.userAccountId?.toString() ?? "",
            actor_id: user?.id?.toString() ?? "",
            reference_id: timeOffRequestId,
            result: { APPROVED: {}, REJECTED: { [employeeId]: [timeOffRequestId] } },
            notes,
            workflow_header_id: data?.alert_json?.WF_HEADER_ID,
            wf_txn_lines_id: data?.alert_json?.WF_TXN_LINES_ID,
        };

        await dispatch(callPhpWorkflow(body));
        refreshData();

        // navigate(-1);
    };

    const approvalStatusTextStyle = getApprovalStatusText(data.time_off_details?.status);

    // ✅ Get the latest APPROVED_ON date from workflow details
    const latestApprovedOn = data.workflow_details
        ?.map((wf) => wf.wf_task_acted_json?.APPROVED_ON)
        ?.filter(Boolean)
        ?.sort((a, b) => new Date(b!).getTime() - new Date(a!).getTime())?.[0];

    const handleCancel = async () => {
        await dispatch(
            cancelTimeOffRequest({
                request_id: data.time_off_details?.time_off_request_id!,
            })
        );

        navigate(-1);
    };

    const handleEdit = async () => {
        dispatch(setTimeoffUpdateMode(true));
        dispatch(
            setTimeoffToUpdate({
                ...(data.time_off_details as any),
                time_off_request_id: data.time_off_details!.time_off_request_id!,
            })
        );

        dispatch(setTopNavNameTitle("Edit Notification"));
        navigate("/m-request-timeoff");
    };

    const getCurrentStep = () => {
        const currentStepCode = data.workflow_current_step_details?.step_code;

        return data.workflow_details?.filter((wf) => wf.step_code === currentStepCode)?.[0];
    };

    const CurrentApproversSection = () => {
        return <ApprovalHistory workflowDetails={data.workflow_details} />;
    };

    return (
        <div className="min-h-full flex flex-col items-center px-4 pb-8">
            {/* Main Card */}
            <div className="w-full max-w-md">
                {/* Date Range */}
                <div className="flex items-start space-x-3 text-gray-900">
                    <div className="flex flex-col">
                        <div className="text-2xl font-bold">
                            {DateTimeUtil.formatDate(data.time_off_details?.start_date, DateTimeUtil.MONTH_DATE)}
                        </div>
                        <div className="text-l text-gray-600">
                            {DateTimeUtil.getDayName(data.time_off_details?.start_date)}
                        </div>
                    </div>
                    <div className="text-2xl font-bold">-</div>
                    <div className="flex flex-col">
                        <div className="text-2xl font-bold">
                            {DateTimeUtil.formatDate(data.time_off_details?.end_date, DateTimeUtil.MONTH_DATE)}
                        </div>
                        <div className="text-l text-gray-600">
                            {DateTimeUtil.getDayName(data.time_off_details?.end_date)}
                        </div>
                    </div>
                </div>

                {/* Requestor Details */}
                <div className="mt-6 flex items-center space-x-3">
                    <NgwAvatar
                        src={data.time_off_details?.requestor?.image_url}
                        name={data.time_off_details?.requestor?.name}
                    />
                    <div>
                        <p className="text-l font-semibold">{data.time_off_details?.requestor?.name}</p>
                        <p className="text-gray-500 text-sm">{data.time_off_details?.notes}</p>
                    </div>
                </div>

                <hr className="my-4 border-gray-300" />

                {/* Leave Summary */}

                {/* Policy Name and Category */}
                <div className="flex flex-wrap gap-2">
                    <div>
                        {data.time_off_details?.amount_requested &&
                            data.time_off_details?.category?.track_time_in &&
                            data.time_off_details?.earning_info?.earnings_code_name && (
                                <div className="px-4 rounded-lg">
                                    <p className="text-md text-gray-600">
                                        <span className="font-medium">
                                            {data.time_off_details.amount_requested}{" "}
                                            {data.time_off_details.category?.track_time_in}
                                        </span>{" "}
                                        of {data.time_off_details.earning_info.earnings_code_name}
                                    </p>
                                </div>
                            )}
                    </div>

                    <div>
                        {/* {data.time_off_details?.policy_name && (
                            <span className="text-sm bg-gray-100 text-gray-700 px-4 py-2 rounded-full">
                                {data.time_off_details.policy_name}
                            </span>
                        )} */}
                        {data.time_off_details?.category?.category_name && (
                            <span className="text-sm bg-blue-100 text-blue-700 px-4 py-2 ml-1 rounded-full">
                                {data.time_off_details.category.category_name}
                            </span>
                        )}
                    </div>
                </div>

                <hr className="my-4 border-gray-300" />

                {/* Request Status */}
                <div className="mt-2 p-4 rounded-lg border">
                    <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 mt-1 text-blue-500" />
                        <div className="text-sm text-gray-700 space-y-1">
                            <p>
                                <span className="text-gray-500">Requested by</span>{" "}
                                <span className="font-medium text-gray-800">
                                    {data.time_off_details?.requestor?.name}
                                </span>
                            </p>
                            <p>
                                <span className="text-gray-500">{latestApprovedOn ? "Approved on" : "Requested from"}</span>{" "}
                                {latestApprovedOn
                                    ? DateTimeUtil.FormatWithDayjsUtc(latestApprovedOn, DateTimeUtil.DATETIME_FANCY_2)
                                    : DateTimeUtil.formatDate(data.time_off_details?.start_date, DateTimeUtil.MONTH_DATE)}
                            </p>
                        </div>
                    </div>

                    {/* Status row */}
                    <div className="mt-4 flex items-center gap-2">
                        {getApprovalStatusCheck(data.time_off_details?.status)}
                        <span className={`font-medium ${approvalStatusTextStyle}`}>
                            {data.time_off_details?.status}
                        </span>
                    </div>
                </div>

                {/* add approvers here.. */}
                {getCurrentStep() && <CurrentApproversSection />}

                <hr className="my-4 border-gray-300" />

                {/* Daily Breakdown */}
                {data.time_off_details?.details_json?.days && data.time_off_details?.details_json?.days?.length > 0 && (
                    <div className="mt-1">
                        <p className="font-semibold text-gray-800 mb-1">Breakdown</p>
                        <div className="space-y-2">
                            {data.time_off_details.details_json.days.map((day, index) => (
                                <div
                                    key={index}
                                    className="px-3 py-2 rounded-lg border border-gray-200 bg-white shadow-sm flex justify-between items-center text-sm"
                                >
                                    <div className="flex flex-col">
                                        <span className="text-gray-700 font-medium">
                                            {DateTimeUtil.formatShortDate(day.date)}
                                        </span>
                                        <span className="text-gray-500 text-xs">
                                            {DateTimeUtil.getDayName(day.date)}
                                        </span>
                                    </div>

                                    <div className="flex flex-col text-right text-gray-600">
                                        {day.start_time && day.end_time && (
                                            <span className="text-xs text-gray-500">
                                                {(() => {
                                                    const resolveBaseDate = (): string | null => {
                                                        if (!day.date) return null;

                                                        if (typeof day.date === "string") {
                                                            const [datePart] = day.date.split(" ");
                                                            return datePart ?? null;
                                                        }

                                                        const dateInstance =
                                                            day.date instanceof Date ? day.date : new Date(day.date);

                                                        if (Number.isNaN(dateInstance.getTime())) return null;

                                                        return DateTimeUtil.formatDateToMySQL(dateInstance, true);
                                                    };

                                                    const baseDate = resolveBaseDate();

                                                    if (!baseDate) return "N/A";

                                                    const range = DateTimeUtil.formatElegantTimeRange(
                                                        `${baseDate} ${day.start_time}`,
                                                        `${baseDate} ${day.end_time}`
                                                    );

                                                    return range || "N/A";
                                                })()}
                                            </span>
                                        )}
                                        <span className="mt-1">
                                            <span className="font-medium">
                                                {data.time_off_details?.category?.track_time_in}:
                                            </span>{" "}
                                            {day.hours ?? 0}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Add note textbox */}
                {getCurrentStep() &&
                    getCurrentStep()?.current_status == "0" &&
                    getCurrentStep()?.actor_json?.[user.id!] &&
                    data?.only_notify_no_action !== "1" && (
                        <div className="mt-4">
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="w-full border-2 rounded-md p-2"
                                placeholder="Add notes"
                            />
                        </div>
                    )}

                {/* Accept/decline buttons */}
                {getCurrentStep() &&
                    getCurrentStep()?.current_status == "0" &&
                    getCurrentStep()?.actor_json?.[user.id!] &&
                    data?.only_notify_no_action !== "1" && (
                        <div>
                            {isCallingWorkflow ? (
                                <Loader2 className="animate-spin text-green-500" />
                            ) : (
                                <div className="mt-4 flex justify-end space-x-4">
                                    <button
                                        className="bg-blue-500 px-4 py-2 rounded-md text-white"
                                        onClick={handleAccept}
                                    >
                                        Accept
                                    </button>
                                    <button
                                        className="bg-red-500 px-4 py-2 rounded-md text-white"
                                        onClick={handleDecline}
                                    >
                                        Decline
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                {/* edit/cancel buttons - if employee is the requestor */}
                {data?.workflow_current_step_details?.current_status == "0" &&
                    data?.time_off_details?.requestor?.employee_id == user.id &&
                    data?.only_notify_no_action !== "1" && (
                        <div className="flex space-x-4 mt-4">
                            {isCallingWorkflow ? (
                                <Loader2 className="animate-spin text-green-500" />
                            ) : (
                                <div className="mt-4 flex justify-end space-x-4">
                                    <button
                                        onClick={handleEdit}
                                        className="flex items-center bg-blue-500 text-white px-4 py-2 rounded-md shadow-md hover:bg-blue-600 transition"
                                    >
                                        <Pencil className="w-5 h-5 mr-2" />
                                        Edit
                                    </button>
                                    <button
                                        onClick={handleCancel}
                                        className="flex items-center bg-red-500 text-white px-4 py-2 rounded-md shadow-md hover:bg-red-600 transition"
                                    >
                                        <XCircle className="w-5 h-5 mr-2" />
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
            </div>
        </div>
    );
};

export default TimeoffNotificationScreen;
